/* ==========================================================================
   SkinTalk.mx - Global JavaScript
   Dermatological Skincare Pharmacy - Mexico

   Table of Contents:
   1. Namespace & Routes
   2. Utility Functions
   3. Event Bus (Pub/Sub)
   4. Custom Element Base Class
   5. Scroll Animation Observer
   6. Body Scroll Lock
   7. Cart Count Updater
   8. Lazy Image Observer
   9. Focus Trap
   10. Initialization
   ========================================================================== */


/* ==========================================================================
   1. NAMESPACE & ROUTES
   ========================================================================== */

window.SkinTalk = window.SkinTalk || {};

/**
 * Shopify route references for AJAX API calls.
 * These map to the Shopify Storefront AJAX API endpoints.
 */
SkinTalk.routes = {
  cart_url: '/cart',
  cart_add_url: '/cart/add',
  cart_change_url: '/cart/change',
  cart_update_url: '/cart/update',
  cart_js_url: '/cart.js',
  predictive_search_url: '/search/suggest',
};


/* ==========================================================================
   2. UTILITY FUNCTIONS
   ========================================================================== */

/**
 * Debounce - Delays function execution until after `wait` ms of inactivity.
 * @param {Function} fn - The function to debounce.
 * @param {number} wait - Delay in milliseconds.
 * @returns {Function} Debounced function.
 */
SkinTalk.debounce = function debounce(fn, wait = 300) {
  let timer = null;

  return function debounced(...args) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, wait);
  };
};

/**
 * Throttle - Ensures function executes at most once per `limit` ms.
 * @param {Function} fn - The function to throttle.
 * @param {number} limit - Minimum interval in milliseconds.
 * @returns {Function} Throttled function.
 */
SkinTalk.throttle = function throttle(fn, limit = 300) {
  let waiting = false;
  let lastArgs = null;

  return function throttled(...args) {
    if (!waiting) {
      fn.apply(this, args);
      waiting = true;

      setTimeout(() => {
        waiting = false;
        if (lastArgs) {
          fn.apply(this, lastArgs);
          lastArgs = null;
        }
      }, limit);
    } else {
      lastArgs = args;
    }
  };
};

/**
 * Returns a standard fetch configuration object.
 * @param {string} type - The content type. Defaults to 'json'.
 * @returns {Object} Fetch config with headers.
 */
SkinTalk.fetchConfig = function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': `application/${type}`,
      'X-Requested-With': 'XMLHttpRequest',
    },
  };
};

/**
 * Format cents into Mexican Peso currency string.
 * Shopify stores prices in cents (e.g., 125000 = $1,250.00 MXN).
 * @param {number} cents - Price in cents.
 * @returns {string} Formatted price string, e.g. "$1,250.00".
 */
SkinTalk.formatMoney = function formatMoney(cents) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  const amount = (Number(cents) || 0) / 100;

  return '$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Pause all playing media elements on the page.
 * Useful when opening modals/drawers to prevent background media playback.
 */
SkinTalk.pauseAllMedia = function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow?.postMessage(
      '{"event":"command","func":"pauseVideo","args":""}',
      '*'
    );
  });

  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow?.postMessage('{"method":"pause"}', '*');
  });

  document.querySelectorAll('video').forEach((video) => {
    video.pause();
  });

  document.querySelectorAll('audio').forEach((audio) => {
    audio.pause();
  });

  document.querySelectorAll('model-viewer').forEach((model) => {
    model.pause?.();
  });
};

/**
 * Serialize a form element into a URL-encoded string.
 * @param {HTMLFormElement} form - The form to serialize.
 * @returns {string} URL-encoded form data string.
 */
SkinTalk.serializeForm = function serializeForm(form) {
  const formData = new FormData(form);
  const params = new URLSearchParams();

  for (const [key, value] of formData.entries()) {
    params.append(key, value);
  }

  return params.toString();
};

/**
 * Generate a unique ID string.
 * @param {string} prefix - Optional prefix for the ID.
 * @returns {string} Unique ID.
 */
SkinTalk.uniqueId = function uniqueId(prefix = 'st') {
  return `${prefix}-${Math.random().toString(36).substring(2, 9)}`;
};


/* ==========================================================================
   3. EVENT BUS (PUB/SUB)
   ========================================================================== */

/**
 * Simple publish/subscribe event system.
 * Allows decoupled communication between components.
 *
 * Supported events:
 *   - 'cart:updated'    - Fired when the cart contents change.
 *   - 'variant:changed' - Fired when a product variant is selected.
 *   - 'drawer:open'     - Fired when a drawer (cart, menu) opens.
 *   - 'drawer:close'    - Fired when a drawer closes.
 *   - 'product:added'   - Fired when a product is added to cart.
 */
SkinTalk.subscribers = {};

/**
 * Publish an event to all subscribers.
 * @param {string} eventName - Name of the event.
 * @param {*} data - Data to pass to subscriber callbacks.
 */
SkinTalk.publish = function publish(eventName, data) {
  const callbacks = SkinTalk.subscribers[eventName];

  if (!callbacks?.length) return;

  callbacks.forEach((callback) => {
    try {
      callback(data);
    } catch (error) {
      console.error(
        `[SkinTalk] Error in subscriber for "${eventName}":`,
        error
      );
    }
  });
};

/**
 * Subscribe to an event.
 * @param {string} eventName - Name of the event to listen for.
 * @param {Function} callback - Function to call when event fires.
 * @returns {Function} Unsubscribe function.
 */
SkinTalk.subscribe = function subscribe(eventName, callback) {
  if (typeof callback !== 'function') {
    console.warn(`[SkinTalk] Subscriber for "${eventName}" is not a function.`);
    return () => {};
  }

  if (!SkinTalk.subscribers[eventName]) {
    SkinTalk.subscribers[eventName] = [];
  }

  SkinTalk.subscribers[eventName].push(callback);

  // Return an unsubscribe function
  return function unsubscribe() {
    const index = SkinTalk.subscribers[eventName]?.indexOf(callback);
    if (index != null && index > -1) {
      SkinTalk.subscribers[eventName].splice(index, 1);
    }
  };
};


/* ==========================================================================
   4. CUSTOM ELEMENT BASE CLASS
   ========================================================================== */

/**
 * Base class for all SkinTalk custom elements.
 * NOTE: The class is defined inline in theme.liquid for synchronous availability.
 * This block only runs if that inline script was somehow skipped.
 */
if (!window.SkinTalkElement) {
  class SkinTalkElement extends HTMLElement {
    constructor() {
      super();
      this._eventCleanup = [];
    }
    connectedCallback() {}
    disconnectedCallback() {
      this._eventCleanup.forEach((cleanup) => cleanup());
      this._eventCleanup = [];
    }
    qs(selector) { return this.querySelector(selector); }
    qsa(selector) { return this.querySelectorAll(selector); }
    on(target, event, handler, options) {
      target.addEventListener(event, handler, options);
      this._eventCleanup.push(() => target.removeEventListener(event, handler, options));
    }
    data(key) { return this.dataset[key]; }
  }
  window.SkinTalkElement = SkinTalkElement;
}


/* ==========================================================================
   5. SCROLL ANIMATION OBSERVER
   ========================================================================== */

/**
 * IntersectionObserver that adds the '.animate-in' class to elements
 * with a [data-animate] attribute when they scroll into view.
 * Respects prefers-reduced-motion.
 */
SkinTalk.initScrollAnimations = function initScrollAnimations() {
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  // If user prefers reduced motion, make everything visible immediately
  if (prefersReducedMotion) {
    document.querySelectorAll('[data-animate]').forEach((el) => {
      el.classList.add('animate-in');
    });
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          observer.unobserve(entry.target);
        }
      });
    },
    {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    }
  );

  document.querySelectorAll('[data-animate]').forEach((el) => {
    observer.observe(el);
  });

  // Store reference so it can be used for dynamically added elements
  SkinTalk._scrollAnimationObserver = observer;
};

/**
 * Observe a dynamically added element for scroll animation.
 * @param {Element} el - Element with [data-animate] attribute.
 */
SkinTalk.observeAnimation = function observeAnimation(el) {
  if (SkinTalk._scrollAnimationObserver) {
    SkinTalk._scrollAnimationObserver.observe(el);
  } else {
    el.classList.add('animate-in');
  }
};


/* ==========================================================================
   6. BODY SCROLL LOCK
   ========================================================================== */

/**
 * Lock body scrolling. Saves current scroll position and prevents
 * page scroll behind modals/drawers.
 */
SkinTalk.lockBodyScroll = function lockBodyScroll() {
  const scrollY = window.scrollY;
  SkinTalk._savedScrollY = scrollY;

  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.overflow = 'hidden';
};

/**
 * Unlock body scrolling. Restores the previous scroll position.
 */
SkinTalk.unlockBodyScroll = function unlockBodyScroll() {
  const scrollY = SkinTalk._savedScrollY ?? 0;

  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.overflow = '';

  window.scrollTo(0, scrollY);
};


/* ==========================================================================
   7. CART COUNT UPDATER
   ========================================================================== */

/**
 * Updates all elements with [data-cart-count] to reflect the current
 * cart item count. Hides the badge if count is 0.
 * @param {number} count - The total number of items in the cart.
 */
SkinTalk.updateCartCount = function updateCartCount(count) {
  const countElements = document.querySelectorAll('[data-cart-count]');

  countElements.forEach((el) => {
    el.textContent = count;

    if (count > 0) {
      el.removeAttribute('hidden');
      el.setAttribute('aria-label', `${count} artículo${count !== 1 ? 's' : ''} en el carrito`);
    } else {
      el.setAttribute('hidden', '');
    }
  });
};

// Listen for cart:updated events to auto-update counts
SkinTalk.subscribe('cart:updated', (cart) => {
  const count = cart?.item_count ?? 0;
  SkinTalk.updateCartCount(count);
});


/* ==========================================================================
   8. LAZY IMAGE OBSERVER
   ========================================================================== */

/**
 * IntersectionObserver for images with a data-src attribute.
 * Acts as a fallback/enhancement alongside native loading="lazy".
 * Swaps data-src to src and data-srcset to srcset when the image
 * enters the viewport.
 */
SkinTalk.initLazyImages = function initLazyImages() {
  // Skip if native lazy loading is well-supported and sufficient
  if ('loading' in HTMLImageElement.prototype) {
    // Still handle data-src images that might exist
    document.querySelectorAll('img[data-src]').forEach((img) => {
      img.src = img.dataset.src;
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
      }
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
    });
    return;
  }

  const lazyObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
            img.removeAttribute('data-srcset');
          }
          img.classList.add('loaded');
          lazyObserver.unobserve(img);
        }
      });
    },
    {
      rootMargin: '200px 0px',
      threshold: 0,
    }
  );

  document.querySelectorAll('img[data-src]').forEach((img) => {
    lazyObserver.observe(img);
  });

  SkinTalk._lazyImageObserver = lazyObserver;
};


/* ==========================================================================
   9. FOCUS TRAP
   ========================================================================== */

/**
 * Trap keyboard focus within a container element.
 * Used for modals, drawers, and other overlay components to ensure
 * keyboard users cannot tab outside the visible content.
 *
 * @param {HTMLElement} container - The element to trap focus within.
 * @param {HTMLElement} [elementToFocus] - Optional element to focus initially.
 */
SkinTalk.trapFocus = function trapFocus(container, elementToFocus) {
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    'details',
    'summary',
    'iframe',
    'object',
    'embed',
    '[contenteditable]',
  ].join(', ');

  const focusableElements = container.querySelectorAll(FOCUSABLE_SELECTOR);
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  // Remove any existing trap handler
  SkinTalk.removeTrapFocus();

  if (focusableElements.length === 0) return;

  SkinTalk._trapFocusHandler = function handleTrapKeydown(event) {
    if (event.key !== 'Tab') return;

    if (event.shiftKey) {
      // Shift+Tab: if focus is on first element, wrap to last
      if (document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      }
    } else {
      // Tab: if focus is on last element, wrap to first
      if (document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    }
  };

  container.addEventListener('keydown', SkinTalk._trapFocusHandler);
  SkinTalk._trapFocusContainer = container;

  // Focus the specified element or the first focusable element
  const targetFocus = elementToFocus || firstFocusable;
  targetFocus?.focus();
};

/**
 * Remove the focus trap from the current container.
 */
SkinTalk.removeTrapFocus = function removeTrapFocus() {
  if (SkinTalk._trapFocusContainer && SkinTalk._trapFocusHandler) {
    SkinTalk._trapFocusContainer.removeEventListener(
      'keydown',
      SkinTalk._trapFocusHandler
    );
  }
  SkinTalk._trapFocusContainer = null;
  SkinTalk._trapFocusHandler = null;
};


/* ==========================================================================
   10. INITIALIZATION
   ========================================================================== */

/**
 * Run all initialization routines when the DOM is ready.
 */
function initSkinTalk() {
  SkinTalk.initScrollAnimations();
  SkinTalk.initLazyImages();

  // Log initialization in development
  if (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.Shopify?.designMode
  ) {
    console.log('[SkinTalk] Global JS initialized.');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSkinTalk);
} else {
  initSkinTalk();
}

// Re-initialize scroll animations in Shopify Theme Editor
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', () => {
    SkinTalk.initScrollAnimations();
  });
}
